import { AppThunk } from 'src/store/store';
import { setPaymentCheckFailed, setPaymentSuccessInfo } from './paymentSlice';
import { buildPaymentApi } from '../../../api';
import { FLOWS_ENUM } from '../../../common/enums';
import { getPaymentCategory } from '../../../common/paymentUtils';
import { PostbackStatus } from '../../../types/payment';

type CompletionCallbackCallback = (param?: boolean) => void | undefined;

export type CompletionCallback<T = boolean> = (
  params?: T
) => CompletionCallbackCallback | void;
export interface PayNothingOutcome {
  success: boolean;
  paymentId: string;
  id: string;
  paymentConfirmation: string;
  errorMessage?: string;
}

export type PayNothingRenewalCallback = (
  successful?: boolean,
  errorMessage?: string
) => CompletionCallbackCallback | void;

// v1 doesn't return a mappable error shape, so its message is only safe to
// show when v2 is active — undefined otherwise, letting the caller fall back
// to its own fixed copy.
const getV2ErrorMessage = (
  pciEnabled: boolean,
  error: unknown
): string | undefined =>
  pciEnabled && error instanceof Error ? error.message : undefined;

export const checkTaxPaymentAsync = (
  paymentID: string,
  completionCallback?: CompletionCallbackCallback
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const api = buildPaymentApi(
      getState().config.settings?.pci_payment_api_enabled ?? false
    );
    try {
      const authToken = getState().auth.idToken;
      const data = await api.checkTaxPayment(authToken, paymentID);
      const previousInfo = getState().payment.paymentSuccesInfo;
      dispatch(
        setPaymentSuccessInfo({
          ...previousInfo,
          ...data,
          paymentConfirmation:
            data.paymentConfirmation ?? previousInfo?.paymentConfirmation,
        })
      );
    } catch {
      dispatch(setPaymentCheckFailed(true));
    } finally {
      if (completionCallback) {
        completionCallback();
      }
    }
  };
};

export const checkPaymentAsync = (
  paymentID: string,
  flow: FLOWS_ENUM | undefined,
  completionCallback: CompletionCallbackCallback
): AppThunk<void> => {
  return async (dispatch) => {
    if (flow === FLOWS_ENUM.PAY_TOT) {
      return dispatch(checkTaxPaymentAsync(paymentID, completionCallback));
    } else if (
      flow === FLOWS_ENUM.APPLY_LICENSE ||
      flow === FLOWS_ENUM.RENEW_LICENSE
    ) {
      return dispatch(
        checkApplicationPaymentAsync(paymentID, completionCallback)
      );
    } else {
      throw new Error(`Unsupported flow: ${flow}`);
    }
  };
};

export const checkApplicationPaymentAsync = (
  paymentID: string,
  completionCallback: CompletionCallbackCallback = () => undefined
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const api = buildPaymentApi(
      getState().config.settings?.pci_payment_api_enabled ?? false
    );
    try {
      const authToken = getState().auth.idToken;
      const data = await api.checkLicensePayment(authToken, paymentID);
      const previousInfo = getState().payment.paymentSuccesInfo;
      dispatch(
        setPaymentSuccessInfo({
          ...previousInfo,
          ...data,
          paymentConfirmation:
            data.paymentConfirmation ?? previousInfo?.paymentConfirmation,
        })
      );
    } catch {
      dispatch(setPaymentCheckFailed(true));
    } finally {
      completionCallback();
    }
  };
};

export const payNothing = (
  emailAddress: string,
  onCompletionCallback: CompletionCallback<PayNothingOutcome>
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const pciEnabled =
      getState().config.settings?.pci_payment_api_enabled ?? false;
    const api = buildPaymentApi(pciEnabled);
    const outcome: PayNothingOutcome = {
      success: false,
      id: '',
      paymentId: '',
      paymentConfirmation: '',
    };
    try {
      const { paymentID, signatoryName, paymentFees } = getState().payment;
      const flow = getState().flow.currentFlow;
      const { paymentId } = await api.payNothing(
        {
          email: emailAddress,
          payment_id: Number(paymentID),
          signatory: signatoryName as string,
          signature: paymentFees?.signature ?? '',
          credits: paymentFees?.summary?.credit ?? 0,
          payment_category: getPaymentCategory(flow!)!,
        },
        getState().auth.idToken
      );
      if (paymentId) {
        const data = await api.checkLicensePayment(
          getState().auth.idToken,
          paymentId.toString()
        );
        const { id, paymentConfirmation, status } = data;
        dispatch(setPaymentSuccessInfo(data));
        if (
          status === PostbackStatus.COMPLETE ||
          (id !== undefined && paymentConfirmation !== undefined)
        ) {
          outcome.success = true;
          outcome.paymentId = paymentId.toString();
          outcome.id = id ?? '';
          outcome.paymentConfirmation = paymentConfirmation ?? '';
        }
      }
    } catch (error) {
      console.error('payNothing error', error);
      outcome.errorMessage = getV2ErrorMessage(pciEnabled, error);
    } finally {
      onCompletionCallback(outcome);
    }
  };
};

export const payNothingTax = (
  emailAddress: string,
  onError?: (errorMessage?: string) => void
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const pciEnabled =
      getState().config.settings?.pci_payment_api_enabled ?? false;
    const api = buildPaymentApi(pciEnabled);
    try {
      const { paymentID, signatoryName, paymentFees } = getState().payment;
      const flow = getState().flow.currentFlow;
      const { paymentId } = await api.payNothing(
        {
          email: emailAddress,
          payment_id: Number(paymentID),
          signatory: signatoryName as string,
          signature: paymentFees?.signature ?? '',
          credits: paymentFees?.summary?.credit ?? 0,
          payment_category: getPaymentCategory(flow!)!,
        },
        getState().auth.idToken
      );

      if (paymentId) {
        dispatch(checkTaxPaymentAsync(paymentID as string));
      }
    } catch (error) {
      onError?.(getV2ErrorMessage(pciEnabled, error));
      dispatch(setPaymentCheckFailed(true));
    }
  };
};

export const payNothingRenewal = (
  emailAddress: string,
  onCompletionCallback: PayNothingRenewalCallback
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const pciEnabled =
      getState().config.settings?.pci_payment_api_enabled ?? false;
    const api = buildPaymentApi(pciEnabled);
    try {
      const { paymentID, signatoryName, paymentFees } = getState().payment;
      const flow = getState().flow.currentFlow;
      const { paymentId } = await api.payNothing(
        {
          email: emailAddress,
          payment_id: Number(paymentID),
          signatory: signatoryName as string,
          signature: paymentFees?.signature ?? '',
          credits: paymentFees?.summary?.credit ?? 0,
          payment_category: getPaymentCategory(flow!)!,
        },
        getState().auth.idToken
      );
      if (paymentId) {
        dispatch(
          checkApplicationPaymentAsync(
            paymentID as string,
            onCompletionCallback(true) as CompletionCallbackCallback
          )
        );
      } else {
        onCompletionCallback(false);
      }
    } catch (error) {
      onCompletionCallback(false, getV2ErrorMessage(pciEnabled, error));
    }
  };
};

export const payCheck = (
  completionCallback: CompletionCallback = () => undefined
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const pciEnabled =
      getState().config.settings?.pci_payment_api_enabled ?? false;
    const api = buildPaymentApi(pciEnabled);
    const { paymentID, signatoryName, paymentFees } = getState().payment;
    const email = getState().auth.userDetails?.email;
    const authToken = getState().auth.idToken;
    const flow = getState().flow.currentFlow;

    try {
      const data = await api.payByCheck(
        {
          email: email as string,
          payment_category: getPaymentCategory(flow!)!,
          payment_id: Number(paymentID),
          signatory: signatoryName as string,
          subtotal: paymentFees?.summary?.total ?? 0,
          signature: paymentFees?.signature ?? '',
          credits: paymentFees?.summary?.credit ?? 0,
        },
        authToken
      );
      dispatch(
        setPaymentSuccessInfo({
          ...data,
          paymentConfirmation: data.checkUniqueCode,
        })
      );
    } catch (error) {
      if (pciEnabled) {
        throw error;
      }
      dispatch(setPaymentCheckFailed(true));
    } finally {
      completionCallback();
    }
  };
};
